'use client'

import {
  type FC,
  startTransition,
  type SubmitEventHandler,
  useActionState,
} from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import { Button, Card, CardContent, Notification } from 'base-ui'

import { type StoryblokMagazineNewsletterSubscriptionType } from '../../storyblok'

import { StoryblokMagazineNewsletterSubscriptionBenefits } from './StoryblokMagazineNewsletterSubscriptionBenefits'
import { StoryblokMagazineNewsletterSubscriptionEmailField } from './StoryblokMagazineNewsletterSubscriptionEmailField'
import { type StoryblokMagazineNewsletterSubscriptionFormData } from './StoryblokMagazineNewsletterSubscriptionFormData'
import {
  storyblokMagazineNewsletterSubscriptionSubscribe,
  type StoryblokNewsletterSubscriptionState,
} from './storyblokMagazineNewsletterSubscriptionSubscribe'
import { StoryblokMagazineNewsletterSubscriptionTos } from './StoryblokMagazineNewsletterSubscriptionTos'

type StoryblokMagazineNewsletterSubscriptionFormProps = Pick<
  StoryblokMagazineNewsletterSubscriptionType,
  'subscriptionType'
>

export const StoryblokMagazineNewsletterSubscriptionForm: FC<
  StoryblokMagazineNewsletterSubscriptionFormProps
> = ({ subscriptionType }) => {
  const [state, formAction, pending] = useActionState<
    StoryblokNewsletterSubscriptionState,
    FormData
  >(storyblokMagazineNewsletterSubscriptionSubscribe, {
    status: 'idle',
  })

  const methods = useForm<StoryblokMagazineNewsletterSubscriptionFormData>({
    progressive: true,
  })

  if (state.status === 'success') {
    return (
      <>
        <Notification severity="success">
          <FormattedMessage id="magazineNewsletterSubscription.success" />
        </Notification>
        <StoryblokMagazineNewsletterSubscriptionBenefits />
      </>
    )
  }

  if (state.status === 'error') {
    return (
      <Notification severity="error">
        <FormattedMessage id="magazineNewsletterSubscription.error" />
      </Notification>
    )
  }

  const { handleSubmit, register } = methods

  /**
   * Initially I tried `Form` component from rhf, but it does not work proper with `useActionState`.
   * Explicitly implement and call `startTransition` to avoid error related when calling action dispatch outside of transition.
   * In case of React `form` `action` it is handled automatically.
   */
  const onSubmit: SubmitEventHandler<HTMLFormElement> = (event) => {
    if (pending) {
      event.preventDefault()

      return
    }

    const formData = new FormData(event.currentTarget)

    void handleSubmit(() => {
      startTransition(() => {
        formAction(formData)
      })
    })(event)
  }

  return (
    <FormProvider {...methods}>
      <Card className="bg-light-brand-high">
        <CardContent className="flex flex-col gap-4">
          <form
            action={formAction}
            className="flex flex-col gap-4"
            noValidate
            onSubmit={onSubmit}
          >
            <p>
              <FormattedMessage id="newsletter.guest.welcomeDiscount.disclaimer" />
            </p>
            <StoryblokMagazineNewsletterSubscriptionEmailField />
            <input
              {...register('subscriptionType')}
              type="hidden"
              value={subscriptionType}
            />
            <Button className="w-full" type="submit">
              <FormattedMessage id="account.newsletter.subscribe.button" />
            </Button>
            <StoryblokMagazineNewsletterSubscriptionTos />
          </form>
          <StoryblokMagazineNewsletterSubscriptionBenefits />
        </CardContent>
      </Card>
    </FormProvider>
  )
}
